import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, AlertTriangle } from 'lucide-react';
import { getWarehouseLocalComponents } from '../utils/timezoneDisplay';

interface LateTimeModalProps {
    open: boolean;
    title: string;
    description: string;
    /** ISO string — pre-fills the input (defaults to current time) */
    defaultTime?: string;
    /** Warehouse timezone */
    warehouseTimeZone?: string;
    /** When true, disables confirm button and shows spinner (prevents double-submit) */
    isSubmitting?: boolean;
    onConfirm: (isoString: string) => void;
    onCancel: () => void;
}

/** Format an ISO string to a local datetime-local value (YYYY-MM-DDTHH:mm) in the target timezone */
function toDatetimeLocal(isoStr: string | undefined, timeZone?: string): string {
    const d = isoStr ? new Date(isoStr) : new Date();
    if (isNaN(d.getTime())) return '';
    const { date, time } = getWarehouseLocalComponents(d.toISOString(), timeZone);
    return `${date}T${time}`;
}

/**
 * A small confirmation modal shown when check-in or check-out is more than the
 * allowed threshold past the scheduled time.  The user verifies / corrects the
 * actual time before submitting.
 */
export function LateTimeModal({ open, title, description, defaultTime, warehouseTimeZone, isSubmitting, onConfirm, onCancel }: LateTimeModalProps) {
    const initial = toDatetimeLocal(defaultTime, warehouseTimeZone);
    const [value, setValue] = useState(initial);
    const [error, setError] = useState<string | null>(null);

    // Rebuild default if modal is re-opened
    useEffect(() => {
        if (open) {
            setValue(toDatetimeLocal(defaultTime, warehouseTimeZone));
            setError(null);
        }
    }, [open, defaultTime, warehouseTimeZone]);

    const maxValue = toDatetimeLocal(new Date().toISOString(), warehouseTimeZone); // can't pick future

    const handleConfirm = () => {
        if (isSubmitting) return;
        if (!value) {
            setError('Please select a time.');
            return;
        }
        // value is YYYY-MM-DDTHH:mm. We pass it exactly as-is so the backend
        // can parse it in the warehouse timezone.
        onConfirm(value);
    };

    if (!open) return null;

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onCancel}
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 16 }}
                        className="relative w-full max-w-sm bg-slate-900 border border-amber-500/40 rounded-2xl shadow-[0_0_40px_rgba(245,158,11,0.15)] p-6 flex flex-col gap-5"
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-500/15 rounded-lg border border-amber-500/30">
                                    <AlertTriangle size={18} className="text-amber-400" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-white">{title}</h2>
                                    <p className="text-xs text-slate-400 mt-0.5 leading-snug">{description}</p>
                                </div>
                            </div>
                            <button
                                onClick={onCancel}
                                className="text-slate-500 hover:text-white transition-colors shrink-0 mt-0.5"
                                aria-label="Cancel"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Time Picker */}
                        <div className="space-y-2">
                            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                <Clock size={12} />
                                Actual Time
                            </label>
                            <input
                                type="datetime-local"
                                value={value}
                                max={maxValue}
                                onChange={(e) => {
                                    setValue(e.target.value);
                                    setError(null);
                                }}
                                className="w-full bg-slate-800 border border-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50 rounded-lg px-3 py-2.5 text-white text-sm outline-none transition-all [color-scheme:dark]"
                            />
                            {error && (
                                <p className="text-xs text-red-400 flex items-center gap-1">
                                    <AlertTriangle size={11} /> {error}
                                </p>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-end gap-3">
                            <button
                                onClick={onCancel}
                                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={isSubmitting}
                                className={`px-5 py-2 bg-amber-500 hover:bg-amber-400 active:scale-95 text-slate-900 font-bold text-sm rounded-lg shadow-lg shadow-amber-500/20 transition-all ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                                {isSubmitting ? 'Submitting…' : 'Confirm Time'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
